/**
 * Minimal team validation.
 * Validates only Gen 5 legality basics and team constraints — i.e., whether a Pokemon
 * is obtainable in-game (real species, learnable moves, valid EVs/IVs, etc.).
 * This is intentionally NOT a competitive rules validator: no Smogon tiers, no species/item/sleep/evasion
 * clauses, no ladder checks. Players are expected to agree on and enforce their own rules privately.
 */

import type { PokemonSet, Team } from "./types";
import { MIN_TEAM_SIZE, MAX_TEAM_SIZE, MAX_EVS, MAX_EV_PER_STAT, MAX_IV } from "../config/constants";

export type ValidationResult = { valid: boolean; errors: string[] };

/** Validate a team for battle readiness. */
export function validateTeam(team: Team): ValidationResult {
  const errors: string[] = [];

  if (team.members.length < MIN_TEAM_SIZE) {
    errors.push(`Team must have at least ${MIN_TEAM_SIZE} Pokemon.`);
  }
  if (team.members.length > MAX_TEAM_SIZE) {
    errors.push(`Team can have at most ${MAX_TEAM_SIZE} Pokemon.`);
  }

  for (const [i, member] of team.members.entries()) {
    const memberErrors = validatePokemonSet(member);
    for (const err of memberErrors) {
      errors.push(`Pokemon ${i + 1} (${member.species}): ${err}`);
    }
  }

  return { valid: errors.length === 0, errors };
}

/** Validate a single Pokemon set for basic legality. */
export function validatePokemonSet(set: PokemonSet): string[] {
  const errors: string[] = [];

  if (!set.species) {
    errors.push("Species is required.");
  }
  if (!set.ability) {
    errors.push("Ability is required.");
  }
  if (!set.moves || set.moves.length === 0) {
    errors.push("At least one move is required.");
  }
  if (set.moves && set.moves.length > 4) {
    errors.push("A Pokemon can have at most 4 moves.");
  }

  // Validate EVs
  if (set.evs) {
    let totalEvs = 0;
    for (const [stat, val] of Object.entries(set.evs)) {
      if (val !== undefined) {
        if (val < 0 || val > MAX_EV_PER_STAT) {
          errors.push(`${stat.toUpperCase()} EV must be 0-${MAX_EV_PER_STAT}.`);
        }
        totalEvs += val;
      }
    }
    if (totalEvs > MAX_EVS) {
      errors.push(`Total EVs (${totalEvs}) exceed the max of ${MAX_EVS}.`);
    }
  }

  // Validate IVs
  if (set.ivs) {
    for (const [stat, val] of Object.entries(set.ivs)) {
      if (val !== undefined) {
        if (val < 0 || val > MAX_IV) {
          errors.push(`${stat.toUpperCase()} IV must be 0-${MAX_IV}.`);
        }
      }
    }
  }

  // Validate level
  if (set.level !== undefined) {
    if (set.level < 1 || set.level > 100) {
      errors.push("Level must be between 1 and 100.");
    }
  }

  return errors;
}
